'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import * as LucideIcons from 'lucide-react';
import { useMemo, type ComponentType } from 'react';
import { cn } from '../utils/cn';
import type { BottomBarProps, BottomBarItem } from './types';

/**
 * Получает компонент иконки из lucide-react по имени
 */
function getLucideIcon(iconName: string): ComponentType<{ className?: string; size?: number }> | null {
  const IconComponent = (LucideIcons as unknown as Record<string, ComponentType<{ className?: string; size?: number }> | undefined>)[iconName];
  return IconComponent || null;
}

/**
 * Определяет тип иконки на основе значения
 */
function detectIconType(icon: string): 'lucide' | 'image' {
  // Если начинается с http://, https:// или /, то это изображение
  if (icon.startsWith('http://') || icon.startsWith('https://') || icon.startsWith('/')) {
    return 'image';
  }
  // Иначе предполагаем, что это имя иконки из lucide-react
  return 'lucide';
}

/**
 * Рендерит иконку в зависимости от типа
 */
function renderIcon(
  item: BottomBarItem,
  className?: string
) {
  if (!item.icon) return null;

  const iconType = item.iconType || detectIconType(item.icon);

  if (iconType === 'lucide') {
    const IconComponent = getLucideIcon(item.icon);
    if (!IconComponent) {
      console.warn(`Icon "${item.icon}" not found in lucide-react`);
      return null;
    }
    return <IconComponent className={className} size={23} />;
  }

  // Изображение
  return (
    <img
      src={item.icon}
      alt={item.label}
      className={className}
      width={23}
      height={23}
    />
  );
}

/**
 * Рендерит бейдж для элемента
 */
function renderBadge(
  item: BottomBarItem,
  badgeClassName?: string
) {
  const badgeValue = item.badge;
  
  if (badgeValue === null || badgeValue === undefined || badgeValue === 0) {
    return null;
  }

  // Если есть кастомный компонент бейджа
  if (item.badgeComponent) {
    const BadgeComponent = item.badgeComponent;
    return (
      <BadgeComponent
        value={badgeValue}
        className={cn(
          'absolute -top-2 -right-2 h-5 min-w-5 flex items-center justify-center px-1 text-xs bg-blue-600 text-white',
          badgeClassName
        )}
      />
    );
  }

  // Стандартный бейдж
  return (
    <span
      className={cn(
        'absolute -top-2 -right-2 h-5 min-w-5 flex items-center justify-center px-1 text-xs bg-blue-600 text-white rounded-full',
        badgeClassName
      )}
    >
      {badgeValue}
    </span>
  );
}

/**
 * Проверяет, активен ли путь
 */
function isPathActive(href: string, pathname: string): boolean {
  if (href === '/') {
    return pathname === '/';
  }
  return pathname.startsWith(href);
}

/**
 * Компонент нижней навигационной панели (BottomBar)
 */
export function BottomBar({
  items,
  className,
  itemClassName,
  activeItemClassName = 'text-blue-600',
  iconClassName,
  labelClassName = 'text-xs',
  badgeClassName,
  showOnMobileOnly = true,
  getTranslation,
  getBadgeValue,
}: BottomBarProps) {
  const pathname = usePathname();

  // Обрабатываем элементы: определяем активные, получаем переводы и бейджи
  const processedItems = useMemo(() => {
    return items.map((item) => {
      // Получаем перевод метки, если функция перевода доступна
      const label = getTranslation
        ? getTranslation(item.label, item.label)
        : item.label;

      // Определяем активность
      const active = item.active !== undefined
        ? item.active
        : isPathActive(item.href, pathname || '/');

      // Получаем значение бейджа
      let badge = item.badge;
      if (getBadgeValue && item.badgeKey && badge === undefined) {
        badge = getBadgeValue(item.badgeKey) ?? null;
      }

      return {
        ...item,
        label,
        active,
        badge,
      };
    });
  }, [items, pathname, getTranslation, getBadgeValue]);

  if (items.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'border-t fixed bottom-0 z-10 w-full bg-white',
        showOnMobileOnly && 'md:hidden',
        className
      )}
    >
      <div
        className={cn(
          'grid h-16 items-center',
          `grid-cols-${items.length}`
        )}
        style={{
          gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))`,
        }}
      >
        {processedItems.map((item, index) => {
          const isActive = item.active;

          return (
            <Link
              key={`${item.href}-${index}`}
              href={item.href}
              className={cn(
                'flex flex-col items-center justify-center text-center',
                item.className,
                itemClassName
              )}
            >
              <div
                className={cn(
                  'flex flex-col items-center justify-center text-center',
                  isActive && activeItemClassName
                )}
              >
                <div className="relative inline-block">
                  {renderIcon(item, iconClassName)}
                  {renderBadge(item, badgeClassName)}
                </div>
                <span className={labelClassName}>{item.label}</span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

